// Pure helpers for the Reviews panel: status labels, star rendering, which
// moderation actions apply, and reply validation. No React/store imports.
import type { ReviewStatus, ReviewView } from '~/lib/api/reviews';
import type { Tone } from '~/components/workbench/tools/payments/payments-model';

export { apiErrorMessage } from '~/components/workbench/tools/payments/payments-model';

export const REVIEW_FILTERS = [
  ['pending', 'Pending'],
  ['approved', 'Approved'],
  ['rejected', 'Rejected'],
] as const;

/** The owner list returns at most this many per page. */
export const OWNER_PAGE_SIZE = 100;

/** Studio's reply limit (reviews.validation LIMITS.reply). */
export const REPLY_MAX = 2000;

const STATUS_META: Record<ReviewStatus, { label: string; tone: Tone }> = {
  pending: { label: 'Pending', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'muted' },
};

export function reviewStatusMeta(status: string): { label: string; tone: Tone } {
  return STATUS_META[status as ReviewStatus] ?? { label: status, tone: 'muted' };
}

/** Five booleans, filled for each whole star of the (clamped, rounded) rating. */
export function starStates(rating: number): boolean[] {
  const n = Number.isFinite(rating) ? Math.min(5, Math.max(0, Math.round(rating))) : 0;
  return Array.from({ length: 5 }, (_, i) => i < n);
}

export function ratingLabel(rating: number): string {
  const n = starStates(rating).filter(Boolean).length;
  return n === 1 ? '1 star' : `${n} stars`;
}

export type ReviewAction = 'approve' | 'reject';

/** Moderation buttons for a review in this status (a status change moves it to another filter). */
export function reviewActions(status: ReviewStatus): ReviewAction[] {
  switch (status) {
    case 'pending':
      return ['approve', 'reject'];
    case 'approved':
      return ['reject'];
    default:
      return ['approve'];
  }
}

/** After a moderation action: drop the review if it no longer matches the filter, else replace it. */
export function applyReviewUpdate(
  list: readonly ReviewView[],
  updated: ReviewView,
  filter: ReviewStatus,
): ReviewView[] {
  return updated.status === filter
    ? list.map((r) => (r.id === updated.id ? updated : r))
    : list.filter((r) => r.id !== updated.id);
}

export function replyError(reply: string): string | null {
  return reply.trim().length > REPLY_MAX ? `Keep the reply under ${REPLY_MAX} characters.` : null;
}

/** "products/linen-shirt" → "linen-shirt"; a page key "/" → "Home page". */
export function subjectLabel(subject: string): string {
  const s = subject.trim();

  if (!s || s === '/') {
    return 'Home page';
  }

  return s.replace(/^\/?products?\//, '');
}

export function emptyReviewsMessage(status: ReviewStatus, mode: 'test' | 'live'): string {
  if (status === 'pending') {
    return mode === 'live'
      ? 'No reviews waiting. Reviews arrive from your published site and stay hidden until you approve them.'
      : 'No test reviews waiting. Reviews left in the preview land here, hidden until you approve them.';
  }

  return status === 'approved' ? 'No approved reviews yet.' : 'No rejected reviews.';
}
